import { css } from '@emotion/react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Flex,
  Icon,
  Stack,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { faPen } from '@inflearn/pro-solid-svg-icons';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { MY_URL } from '../../../constants/urls';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';

type Props = {
  name: string;
  thumbnailUrl: string;
  children?: ReactNode;
  slug?: string;
};

export function UserProfile({ name, thumbnailUrl, children, slug }: Props) {
  const { t } = useTranslation();
  const { isMe } = useCheckMyProfile();

  return (
    <Flex
      gap={12}
      w="100%"
      align="center"
      css={{
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {isMe ? (
        <Link href={MY_URL.ACCOUNT()} aria-label={t('인프런 프로필 설정 페이지로 이동')}>
          <Box pos="relative">
            <Avatar
              src={`${thumbnailUrl}?w=${84 * 2}`}
              alt={name + t('님의 프로필')}
              size={'xl'}
              radius="100%"
              color="gray.3"
              css={stylesProfile}
            />

            <ActionIcon
              size={24}
              radius="xl"
              color="dark.6"
              variant="filled"
              pos="absolute"
              right={0}
              bottom={0}
            >
              <Icon icon={faPen} color="white" size="2xs" />
            </ActionIcon>
          </Box>
        </Link>
      ) : (
        <Avatar
          src={`${thumbnailUrl}?w=${84 * 2}`}
          alt={name + t('님의 프로필')}
          size={'xl'}
          radius="100%"
          color="gray.3"
          css={stylesProfile}
        />
      )}
      <Box
        w="100%"
        css={{
          textAlign: 'center',
        }}
      >
        <Stack mb={6} align={'center'}>
          <Text
            size={'xl'}
            weight={700}
            lineHeight="1.5"
            color="gray.9"
            lineClamp={2}
            css={{
              wordBreak: 'break-all',
            }}
          >
            {name}
          </Text>
          {slug && (
            <Badge variant="light" color="gray" size="md" radius="xl">
              @{slug}
            </Badge>
          )}
        </Stack>
        {children}
      </Box>
    </Flex>
  );
}

const stylesProfile = css({
  [desktopFirstMediaQuery.mobile]: {
    width: 64,
    height: 64,
    minWidth: 64,
  },
});
